/* Build in libraries */
import { useState } from "react";

/* Third party libraries */

/* Components */
import TotalApplicationsWidget from "components/Rover/Widgets/TotalApplicationsWidget";
import Card from "components/UI/Card";
import DoughnutChart from "components/UI/Charts/DoughnutChart";
import AverageHouseholdIncome from "components/Rover/Widgets/AverageHouseholdIncome";
import PieChart from "components/UI/Charts/PieChart";
import { BDPAApprovedDashboard } from "./BDPAApprovedDashboard";

/* Services */
import { EApplicationStatus } from "features/ApplicationForm/constants";
import { convertEnumToText, isObjectEmpty } from "utils";

/* Assets */

interface IApplicationDashboardComponentProps {
  data: any;
}

interface IDoughnutChart {
  dataLabels: string[];
  dataSets: { data: number; color: string }[];
}

interface IPieChart {
  label: string[];
  dataSets: { data: number; color: string }[];
}

export const MDPADashboard = ({
  data,
}: IApplicationDashboardComponentProps) => {
  const [viewStatus, setViewStatus] = useState<string>("ALL");
  const applicationsList = data?.data;

  const widgetsData: {
    totalApplications: number;
    totalHousePrice: number;
    statusSummary: { [key: string]: number };
    totalCityEmployees: IDoughnutChart;
    gender: IDoughnutChart;
    totalHouseholdIncome: number;
    maritalStatus: IPieChart;
  } = {
    totalApplications: 0,
    totalHousePrice: 0,
    statusSummary: {},
    totalCityEmployees: {
      dataLabels: ["Yes", "No"],
      dataSets: [
        { data: 0, color: "#D9B64E" },
        { data: 0, color: "#0FCA98" },
      ],
    },
    gender: {
      dataLabels: ["Female", "Male"],
      dataSets: [
        { data: 0, color: "#D9B64E" },
        { data: 0, color: "#0FCA98" },
      ],
    },
    totalHouseholdIncome: 0,
    maritalStatus: {
      label: ["Married", "Unmarried", "Widowed"],
      dataSets: [
        { data: 0, color: "#0FCA98" },
        { data: 0, color: "#D9B64E" },
        { data: 0, color: "#2B6CB0" },
      ],
    },
  };

  applicationsList?.forEach((a: any) => {
    const { applicationData } = a;
    //Total Application Widget
    widgetsData["totalApplications"] += 1;

    // Total Amount Requested
    widgetsData["totalHousePrice"] += applicationData?.propertyInformation
      ?.housePrice
      ? applicationData?.propertyInformation?.housePrice
      : 0;

    //Status Summary
    widgetsData["statusSummary"][a.status] =
      (widgetsData["statusSummary"][a.status] ?? 0) + 1;

    if (applicationData?.application?.cityEmployee === "yes") {
      widgetsData.totalCityEmployees.dataSets[0].data += 1;
    }
    if (applicationData?.application?.cityEmployee === "no") {
      widgetsData.totalCityEmployees.dataSets[1].data += 1;
    }

    // gender
    if (applicationData?.application?.gender === "Female") {
      widgetsData.gender.dataSets[0].data += 1;
    }
    if (applicationData?.application?.gender === "Male") {
      widgetsData.gender.dataSets[1].data += 1;
    }

    // marital status
    if (applicationData?.application?.maritalStatus === "Un-Married") {
      widgetsData.maritalStatus.dataSets[1].data += 1;
    }
    if (applicationData?.application?.maritalStatus === "Married") {
      widgetsData.maritalStatus.dataSets[0].data += 1;
    }
    if (applicationData?.application?.maritalStatus === "Widow") {
      widgetsData.maritalStatus.dataSets[2].data += 1;
    }

    // Total Household Income
    widgetsData["totalHouseholdIncome"] +=
      applicationData?.householdIncome.reduce((acc: any, curr: any) => {
        // otherIncome: '';
        // pensions: '';
        // publicAssistance: '';
        // relation: 'Sibling';
        // wages: '';
        const wages = curr?.wages !== "" ? Number(curr?.wages) : 0;
        const pensions = curr?.pensions !== "" ? Number(curr?.pensions) : 0;
        const publicAssistance =
          curr?.publicAssistance !== "" ? Number(curr?.publicAssistance) : 0;
        const otherIncome =
          curr?.otherIncome !== "" ? Number(curr?.otherIncome) : 0;
        return (acc += wages + pensions + publicAssistance + otherIncome);
      }, 0);
  });

  const convertIntoPercentage = (
    arrayOfData: { data: number; color: string }[]
  ) => {
    const totalData = arrayOfData.reduce(
      (acc: number, currentData: { data: number; color: string }) => {
        return (acc += currentData.data);
      },
      0
    );
    const percentage = arrayOfData?.map(
      (d: { data: number; color: string }) => {
        const convertNumberIntoPercentage = (d.data * 100) / totalData;
        const convertFloatToInteger = Math.ceil(convertNumberIntoPercentage);
        return {
          ...d,
          data: convertFloatToInteger ? convertFloatToInteger : 0,
        };
      }
    );

    return percentage;
  };

  return (
    <>
      <div className="max-w-screen-3xl mx-auto">
        <div className="font-semibold text-bold py-4 rounded-md space-x-2">
          <span
            className={`${
              viewStatus === "ALL"
                ? "bg-primaryColor-normal"
                : "bg-neutralColor-light hover:bg-primaryColor-normal"
            } text-white bg-primaryColor-normal px-4 py-2 cursor-pointer`}
            onClick={() => setViewStatus("ALL")}>
            All Applications
          </span>
          <span
            className={`${
              viewStatus === "APPROVED"
                ? "bg-primaryColor-normal"
                : "bg-neutralColor-light hover:bg-primaryColor-normal"
            } text-white bg-primaryColor-normal px-4 py-2 cursor-pointer`}
            onClick={() => setViewStatus("APPROVED")}>
            Approved Applications
          </span>
        </div>

        <div className="my-4">
          {/* <Card title='Project Location'>
            {false && (
              <div className='w-full flex justify-center items-center h-screen/2'>
                <div className='flex justify-center items-center gap-2 text-primaryColor-normal'>
                  <LocationMarkerIcon className='h-8 w-8' />
                  <h1>No Projects</h1>
                </div>
                <p>There are no assigned projects to display.</p>
              </div>
            )}
            {true && (
              <div className='w-full grid grid-cols-6 h-screen/2'>
                <div className='col-span-6 w-full border-l p-5'>
                  <MapGoogle projectList={dashboardData?.project_location} />
                </div>
              </div>
            )}
          </Card> */}
        </div>
        {viewStatus === "ALL" && (
          <>
            <div className="my-4">
              <section className="my-4 grid md:grid-cols-2 xl:grid-cols-4 gap-4">
                <TotalApplicationsWidget
                  count={widgetsData?.totalApplications ?? 0}
                />
                <AverageHouseholdIncome
                  count={widgetsData?.totalHouseholdIncome ?? 0}
                />
              </section>
              <Card title="Status Summary">
                <>
                  <div className="grid grid-cols-8 gap-6 py-6 px-6">
                    {!isObjectEmpty(EApplicationStatus) &&
                      Object.keys(EApplicationStatus).map((s: any) => (
                        <div className="col-span-2 font-medium flex justify-between gap-6">
                          <span>
                            {convertEnumToText(
                              EApplicationStatus[
                                s as keyof typeof EApplicationStatus
                              ]
                            )}
                          </span>
                          <span>{widgetsData["statusSummary"][s] ?? 0}</span>
                        </div>
                      ))}
                  </div>
                </>
              </Card>
              <section className="grid md:grid-cols-2 xl:grid-cols-2 gap-4 mt-4">
                <Card title="City Employees">
                  <div className="p-5 flex flex-col items-center ">
                    <div className="w-5/6 lg:w-2/3 ">
                      <DoughnutChart
                        dataLabels={widgetsData?.totalCityEmployees?.dataLabels}
                        datasets={convertIntoPercentage(
                          widgetsData?.totalCityEmployees?.dataSets
                        )}
                        percentage={true}
                      />
                    </div>
                  </div>
                </Card>
                <Card title="Gender">
                  <div className="p-5 flex flex-col items-center ">
                    <div className="w-5/6 lg:w-2/3 ">
                      <DoughnutChart
                        dataLabels={widgetsData?.gender?.dataLabels}
                        datasets={convertIntoPercentage(
                          widgetsData?.gender?.dataSets
                        )}
                        percentage={true}
                      />
                    </div>
                  </div>
                </Card>
              </section>
              <section className="grid md:grid-cols-2 xl:grid-cols-2 gap-4 mt-4">
                <Card title="Marital Status">
                  <div className="p-5 flex flex-col items-center ">
                    <div className="w-5/6 lg:w-2/3 h-80">
                      <PieChart
                        labels={widgetsData?.maritalStatus?.label}
                        datasets={widgetsData?.maritalStatus?.dataSets}
                        chartTitle=""
                      />
                    </div>
                  </div>
                </Card>
              </section>
            </div>
          </>
        )}
        {viewStatus === "APPROVED" && <BDPAApprovedDashboard data={data} />}
      </div>
    </>
  );
};
